import React from 'react';
import classNames from 'classnames';
import Tooltip from '@atlaskit/tooltip';
import CopyIcon from '@atlaskit/icon/glyph/copy';
import CompleteIcon from '@atlaskit/icon/glyph/check-circle';

import { v4 as uuidv4 } from 'uuid';
import { ExitingPersistence, SlideIn } from '@atlaskit/motion';

export interface CopyableTextProps {
  text?: string | null;
  children?: React.ReactChild | HTMLElement;
  attribute?: string;
  duration?: number;
}

const CopyableText: React.FC<CopyableTextProps> = ({ text, attribute = null, duration = 1000, children = null }) => {
  const [isCopyButtonVisible, setIsCopyButtonVisible] = React.useState<boolean>(false);

  const [hasCopiedText, setHasCopiedText] = React.useState<boolean>(false);

  const fieldId = `text-content-${uuidv4()}`;

  const copyText = () => {
    const element = document.getElementById(fieldId);
    if (element) {
      copyContent(element);
      setHasCopiedText(true);
    }
  };

  const copyContent = async (element: HTMLElement) => {
    if (attribute === 'href') {
      const anchorElement = element.children[0].getAttribute('href');
      navigator.clipboard.writeText(`${location.host}${anchorElement}`);
    } else if (attribute) {
      navigator.clipboard.writeText(element.children[0].getAttribute(attribute) || '');
    } else {
      navigator.clipboard.writeText((await document.getElementById(fieldId)?.innerText) || '');
    }
  };

  const setupCopyableContext = () => {
    setIsCopyButtonVisible(true);
    setHasCopiedText(false);
  };

  const resetContent = () => {
    setIsCopyButtonVisible(false);
    setHasCopiedText(false);
  };

  return (
    <div onMouseEnter={setupCopyableContext} onMouseLeave={resetContent}>
      {text && <span id={fieldId}>{text}</span>}
      {children && <span id={fieldId}>{children}</span>}
      <ExitingPersistence>
        {isCopyButtonVisible && (
          <SlideIn enterFrom={'left'} exitTo={'left'} duration={duration}>
            {(props) => (
              <span {...props} className={classNames('copyable-text-container', props.className)}>
                {!hasCopiedText ? (
                  <Tooltip content="Copy text" tag="span">
                    <span data-testid="copyable-text-icon" onClick={() => copyText()} className={'copyable-text-icon'}>
                      <CopyIcon label={'Copy text'} size={'small'} />
                    </span>
                  </Tooltip>
                ) : (
                  <Tooltip content="Copied text" tag="span">
                    <span className={'copied-text-icon'}>
                      <CompleteIcon label={'Copied text'} size={'small'} />
                    </span>
                  </Tooltip>
                )}
              </span>
            )}
          </SlideIn>
        )}
      </ExitingPersistence>
    </div>
  );
};

export default CopyableText;
